import { Injectable } from '@angular/core';
import * as signalR from "@microsoft/signalr"
import { IHttpConnectionOptions, IRetryPolicy, RetryContext } from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EventNotficationMessage } from '../model/eventNotficationMessage';
import { UserSessionService } from './user-session.service';

const EVENTS_HUB_METHOD = 'residentEvents';
const RESTART_DELAY_MS = 5000;

/**
 * The default reconnect policy gives up after 4 attempts (~30 seconds), after which the
 * dashboard stays silently frozen for the rest of the shift. A carer has no way of knowing
 * the screen has stopped updating, so we keep retrying for as long as the page is open.
 */
export class NeverGiveUpRetryPolicy implements IRetryPolicy {
	nextRetryDelayInMilliseconds(retryContext: RetryContext): number {
		const attempt = retryContext.previousRetryCount;

		if (attempt === 0) {
			return 0;
		}

		if (attempt < 3) {
			return 2000;
		}

		if (attempt < 6) {
			return 5000;
		}

		if (attempt < 12) {
			return 15000;
		}

		return 30000;
	}
}

@Injectable({
	providedIn: 'root'
})

export class SignalrService {
	private hubConnection: signalR.HubConnection
	private restartTimeoutId: number;
	private stopped: boolean = true;

	eventNotficationMessage: Subject<EventNotficationMessage>;
	reconnected: BehaviorSubject<string>;
	connected: BehaviorSubject<boolean>;

	constructor(private userSessionService: UserSessionService) {
		this.eventNotficationMessage = new Subject<EventNotficationMessage>();
		this.reconnected = new BehaviorSubject<string>(null);
		this.connected = new BehaviorSubject<boolean>(false);
	}

	public startConnection = () => {
		this.stopped = false;

		if (this.hubConnection && this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
			return;
		}

		const options: IHttpConnectionOptions = {
			withCredentials: true,
		}

		this.hubConnection = new signalR.HubConnectionBuilder()

			.withUrl(environment.workerUrl, options)
			.withAutomaticReconnect(new NeverGiveUpRetryPolicy())
			.configureLogging(signalR.LogLevel.Debug)
			.build();

		this.registerHandlers();

		this.hubConnection.onreconnecting(() => {
			this.connected.next(false);
		});

		this.hubConnection.onreconnected((connectionId: string) => {
			this.connected.next(true);
			this.reconnected.next(connectionId);
		});

		// withAutomaticReconnect only covers drops on an already-established connection, and
		// only until its policy is exhausted. Once onclose fires the connection is dead for
		// good, so rebuild it ourselves rather than leaving the client stale.
		this.hubConnection.onclose(() => {
			this.connected.next(false);
			this.scheduleRestart();
		});

		this.start();
	}

	public subscribeToEventsUpdate = () => {
		if (!this.hubConnection) {
			return;
		}

		this.registerHandlers();
	}

	public disconnect = () => {
		this.stopped = true;
		this.clearScheduledRestart();

		if (this.hubConnection) {
			this.hubConnection.off(EVENTS_HUB_METHOD);
			this.connected.next(false);
			return this.hubConnection.stop();
		}

		return Promise.resolve();
	}

	private start() {
		this.hubConnection
			.start()
			.then(() => {
				console.log('Connection started');
				this.connected.next(true);
				// Anything that happened while we were disconnected was never pushed to us,
				// so treat every successful (re)connect as a signal to re-sync from the API.
				this.reconnected.next(this.hubConnection.connectionId);
			})
			.catch(err => {
				console.error('Error while starting connection: ' + err);
				this.scheduleRestart();
			});
	}

	private registerHandlers() {
		this.hubConnection.off(EVENTS_HUB_METHOD);
		this.hubConnection.on(EVENTS_HUB_METHOD, (message: EventNotficationMessage) => {
			this.eventNotficationMessage.next(message);
		});
	}

	private scheduleRestart(delayMs: number = RESTART_DELAY_MS) {
		if (this.stopped || this.restartTimeoutId) {
			return;
		}

		this.restartTimeoutId = window.setTimeout(() => {
			this.restartTimeoutId = undefined;

			if (this.stopped) {
				return;
			}

			this.startConnection();
		}, delayMs);
	}

	private clearScheduledRestart() {
		if (this.restartTimeoutId) {
			clearTimeout(this.restartTimeoutId);
			this.restartTimeoutId = undefined;
		}
	}
}
